import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@design-system/Sheet';
import { useGetScoreReference } from '@viome/viome-base/src/usecase/Health/HealthQuery.ts';
import { isHexColor } from '@utils/checkHexCode.ts';
import { InfoIcon } from '@/components/icons/InfoIcon';
import { sortHealthReferenceByRankScore } from '@/utils/sortHealthReferenceByRankScore';
import { cn } from '@utils/styles';

interface HealthReferenceSectionProps {
  scoreId: number;
  variant?: string;
  'data-testid'?: string;
}

export const HealthReferenceSection = ({
  scoreId,
  variant='dark',
  'data-testid': testId,
}: HealthReferenceSectionProps) => {
  const { data } = useGetScoreReference(scoreId);

  const { score_range: scoreRange = [], score_samples: scoreSamples = [] } =
    data || {};
  const sortedScoreRanges = sortHealthReferenceByRankScore(scoreRange);


  return (
    <Sheet data-testid={testId}>
      <SheetTrigger className="mr-1.5 flex items-center" data-testid="healthL2Details-referenceTrigger">
        <div className={cn("pr-1", variant === 'light' && 'text-secondary typography-btn-small')} data-testid="healthL2Details-referenceText">
          Reference
        </div>
        <InfoIcon
          className="h-2.5 w-2.5"
          aria-label="Information about the reference score"
          data-testid="healthL2Details-referenceIcon"
        />
      </SheetTrigger>
      <SheetContent className="dark w-full md:w-[400px]" data-testid="healthL2Details-referenceSheet">
        <SheetHeader data-testid="healthL2Details-referenceSheetHeader">
          <SheetTitle data-testid="healthL2Details-referenceSheetTitle">Score Reference</SheetTitle>
          <SheetDescription className="pl-0" data-testid="healthL2Details-referenceSheetDescription">
            <>
              {sortedScoreRanges?.length > 0 && (
                <>
                  <div className="typography-title" data-testid="healthL2Details-scoreRangesTitle">Score Ranges</div>
                  {sortedScoreRanges?.map((range, index) => {
                    const {
                      high_range,
                      low_range,
                      percentile,
                      rank,
                      rank_color,
                    } = range || {};
                    return (
                      <div className="mt-6" key={`score-range-${index}`} data-testid={`healthL2Details-scoreRange-${index}`}>
                        <div className="flex items-center" data-testid={`healthL2Details-scoreRangeDetails-${index}`}>
                          <div
                            style={{
                              color: isHexColor(rank_color)
                                ? rank_color
                                : undefined,
                            }}
                            className="typography-body mr-3"
                            data-testid={`healthL2Details-scoreRangeRank-${index}`}
                          >
                            {rank}
                          </div>
                          <div className="typography-body" data-testid={`healthL2Details-scoreRangeValues-${index}`}>
                            {low_range} - {high_range}
                          </div>
                        </div>
                        <div className="typography-body mt-3" data-testid={`healthL2Details-scoreRangePercentile-${index}`}>
                          Represents {percentile}% of the Viome population
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </>
            {scoreSamples?.length > 0 && (
              <>
                <div className="typography-title pt-10 mb-4" data-testid="healthL2Details-sampleAnalyzedTitle">
                  Sample Analyzed
                </div>
                <span className="typography-body" data-testid="healthL2Details-sampleAnalyzedList">{scoreSamples?.join()}</span>
              </>
            )}
          </SheetDescription>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
};
